var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import { useDataContext } from '@/shared/providers/DataProvider';
import SignalRService from '@/shared/services/SignalRService';
import ToastrService from '@/shared/services/ToastrService';
import DataCacheService from '@/shared/services/DataCacheService';
import AuthService, { privileges } from '@/shared/services/AuthService';
import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import { useQueryClient } from '@tanstack/react-query';
import { useGetCompanySettings } from '@/shared/hooks/settings/useGetCompanySettings';
import { useUpdateCompanySettings } from '@/shared/hooks/settings/useUpdateCompanySettings';
import { useGetHrmSettings } from '@/shared/hooks/settings/useGetHrmSettings';
import { useUpdateHrmSettings } from '@/shared/hooks/settings/useUpdateHrmSettings';
import { useUpdateComplianceSettings } from '@/shared/hooks/settings/useUpdateComplianceSettings';
import { useGetDocumentSettings } from '@/shared/hooks/settings/useGetDocumentSettings';
import { useUpdateDocumentSettings } from '@/shared/hooks/settings/useUpdateDocumentSettings';
import { useGetCompanyTemplates } from '@/shared/hooks/settings/useGetCompanyTemplates';
import { SettingsQueryKey } from '@/shared/hooks/settings/queryKey';
import useGetRegistrationResources from '@/shared/hooks/resource/useGetRegistrationResources';
import config from '@/config';
import SettingsCompanySelector from './components/SettingsCompanySelector';
import CompanyHeader from './components/CompanyHeader';
import BudgetSettingsSection from './components/BudgetSettingsSection';
import ComplianceSection from './components/ComplianceSection';
import HrmSettingsSection from './components/HrmSettingsSection';
import DocumentsSection from './components/DocumentsSection';
import PaymentMonitoringSection from './components/PaymentMonitoringSection';
import TaskManagementSection from './components/TaskManagementSection';
import InvoicePdfSection from './components/InvoicePdfSection';
import BillingEmailsSection from './components/BillingEmailsSection';
import BillingSetupSection from './components/BillingSetupSection';
import GeneralSection from './components/GeneralSection';
import messages from './generalSettingsMessages';
var getHideLedgerSetting = function () {
    var _a;
    var createdOn = (_a = DataCacheService.getDataCache()) === null || _a === void 0 ? void 0 : _a.createdOn;
    if (!createdOn)
        return false;
    var releaseDate = moment('08-11-2021', 'DD-MM-YYYY');
    return moment.utc(createdOn).isAfter(releaseDate);
};
var GeneralSettingsContainer = function () {
    var intl = useIntl();
    var queryClient = useQueryClient();
    var _a = useDataContext(), companies = _a.companies, defaultCompany = _a.defaultCompany;
    var _b = useState(function () { var _a, _b, _c; return (_c = (_a = defaultCompany === null || defaultCompany === void 0 ? void 0 : defaultCompany.id) !== null && _a !== void 0 ? _a : (_b = companies === null || companies === void 0 ? void 0 : companies[0]) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : ''; }), selectedCompanyId = _b[0], setSelectedCompanyId = _b[1];
    var selectedCompany = (companies || []).find(function (c) { return c.id === selectedCompanyId; });
    var connectWebUrl = selectedCompanyId
        ? "".concat(config.appUrls.connect, "?companyId=").concat(selectedCompanyId)
        : config.appUrls.connect;
    var canReadSettings = AuthService.hasPermission({
        functionality: 'Settings',
        privilege: privileges.Read,
    });
    var canReadHrmSettings = canReadSettings ||
        AuthService.hasPermission({ functionality: 'Users', privilege: privileges.Update });
    var _c = useGetCompanySettings(selectedCompanyId, canReadSettings), settings = _c.data, isLoading = _c.isLoading;
    var updateCompanySettings = useUpdateCompanySettings().updateCompanySettings;
    var _d = useGetHrmSettings(selectedCompanyId, canReadHrmSettings), hrmSettings = _d.data, isHrmLoading = _d.isLoading;
    var updateHrmSettings = useUpdateHrmSettings().updateHrmSettings;
    var updateComplianceSettings = useUpdateComplianceSettings().updateComplianceSettings;
    var _e = useGetDocumentSettings(canReadSettings), documentSettings = _e.data, isDocSettingsLoading = _e.isLoading;
    var updateDocumentSettings = useUpdateDocumentSettings().updateDocumentSettings;
    var templates = useGetCompanyTemplates().data;
    var resources = useGetRegistrationResources(0).data;
    var handleCompanyChange = useCallback(function (company) {
        setSelectedCompanyId(company.id);
    }, []);
    var handleFieldUpdate = useCallback(function (field, value) { return __awaiter(void 0, void 0, void 0, function () {
        var model, _a;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!settings)
                        return [2 /*return*/];
                    if (settings[field] === value)
                        return [2 /*return*/];
                    model = __assign(__assign({}, settings), (_b = {}, _b[field] = value, _b));
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, updateCompanySettings(model)];
                case 2:
                    _c.sent();
                    ToastrService.success(intl.formatMessage(messages.msgChangesSavedForName, { name: selectedCompany === null || selectedCompany === void 0 ? void 0 : selectedCompany.name }));
                    return [3 /*break*/, 4];
                case 3:
                    _a = _c.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [settings, updateCompanySettings, intl, selectedCompany]);
    var handleHrmFieldUpdate = useCallback(function (field, value) { return __awaiter(void 0, void 0, void 0, function () {
        var model, _a;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!hrmSettings)
                        return [2 /*return*/];
                    if (hrmSettings[field] === value)
                        return [2 /*return*/];
                    model = __assign(__assign({}, hrmSettings), (_b = {}, _b[field] = value, _b));
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, updateHrmSettings({ companyId: selectedCompanyId, model: model })];
                case 2:
                    _c.sent();
                    ToastrService.changesSaved(intl);
                    return [3 /*break*/, 4];
                case 3:
                    _a = _c.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [hrmSettings, selectedCompanyId, updateHrmSettings, intl]);
    var handleDocFieldUpdate = useCallback(function (field, value) { return __awaiter(void 0, void 0, void 0, function () {
        var model, _a;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!documentSettings)
                        return [2 /*return*/];
                    if (documentSettings[field] === value)
                        return [2 /*return*/];
                    model = __assign(__assign({}, documentSettings), (_b = {}, _b[field] = value, _b));
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, updateDocumentSettings(model)];
                case 2:
                    _c.sent();
                    ToastrService.success(intl.formatMessage(messages.msgConfirmChange));
                    return [3 /*break*/, 4];
                case 3:
                    _a = _c.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [documentSettings, updateDocumentSettings, intl]);
    var handleDocSettingsUpdate = useCallback(function (partialSettings) { return __awaiter(void 0, void 0, void 0, function () {
        var model, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!documentSettings)
                        return [2 /*return*/];
                    model = __assign(__assign({}, documentSettings), partialSettings);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, updateDocumentSettings(model)];
                case 2:
                    _b.sent();
                    ToastrService.success(intl.formatMessage(messages.msgConfirmChange));
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [documentSettings, updateDocumentSettings, intl]);
    var handleComplianceUpdate = useCallback(function (setting) { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    _b.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, updateComplianceSettings(setting)];
                case 1:
                    _b.sent();
                    if (setting.shouldTrack) {
                        ToastrService.success(intl.formatMessage(messages.msgComplianceRecalculationStarted));
                    }
                    else {
                        ToastrService.success(intl.formatMessage(messages.msgConfirmChange));
                    }
                    return [3 /*break*/, 3];
                case 2:
                    _a = _b.sent();
                    return [3 /*break*/, 3];
                case 3: return [2 /*return*/];
            }
        });
    }); }, [updateComplianceSettings, intl]);
    var handleFieldUpdateWithConfirm = useCallback(function (field, value, propagate) { return __awaiter(void 0, void 0, void 0, function () {
        var model, _a;
        var _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    if (!settings)
                        return [2 /*return*/];
                    model = __assign(__assign({}, settings), (_b = {}, _b[field] = value, _b.propagateLedgerChanges = propagate, _b));
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, updateCompanySettings(model)];
                case 2:
                    _c.sent();
                    ToastrService.success(intl.formatMessage(messages.msgChangesSavedForName, { name: selectedCompany === null || selectedCompany === void 0 ? void 0 : selectedCompany.name }));
                    return [3 /*break*/, 4];
                case 3:
                    _a = _c.sent();
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [settings, updateCompanySettings, intl, selectedCompany]);
    var handleEmailDomainVerify = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var response, result, model, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(settings === null || settings === void 0 ? void 0 : settings.companyId))
                        return [2 /*return*/];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, api.base.get("/api/settings/VerifyEmailDomain?id=".concat(settings.companyId))];
                case 2:
                    response = _a.sent();
                    result = response.data;
                    model = __assign(__assign({}, settings), { emailUseCustomFrom: result.status === 1, emailDomain: result });
                    return [4 /*yield*/, updateCompanySettings(model)];
                case 3:
                    _a.sent();
                    if (result.status === 0) {
                        ToastrService.error(intl.formatMessage(messages.msgEmailDomainVerificationFailed, {
                            domain: result.domain,
                        }));
                    }
                    else if (result.status === 1) {
                        ToastrService.success(intl.formatMessage(messages.msgEmailDomainVerificationSucceeded, {
                            domain: result.domain,
                        }));
                    }
                    return [3 /*break*/, 5];
                case 4:
                    error_1 = _a.sent();
                    handleHttpError(intl)(error_1);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [settings, updateCompanySettings, intl]);
    var handleEmailDomainDelete = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var model, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!(settings === null || settings === void 0 ? void 0 : settings.companyId))
                        return [2 /*return*/];
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, , 5]);
                    return [4 /*yield*/, api.base.delete("/api/settings/DeleteEmailDomain?id=".concat(settings.companyId))];
                case 2:
                    _a.sent();
                    model = __assign(__assign({}, settings), { emailUseCustomFrom: false, emailDomain: undefined });
                    return [4 /*yield*/, updateCompanySettings(model)];
                case 3:
                    _a.sent();
                    ToastrService.success(intl.formatMessage(messages.msgConfirmChange));
                    return [3 /*break*/, 5];
                case 4:
                    error_2 = _a.sent();
                    handleHttpError(intl)(error_2);
                    return [3 /*break*/, 5];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [settings, updateCompanySettings, intl]);
    var handleEmailDomainAdded = useCallback(function (domain) { return __awaiter(void 0, void 0, void 0, function () {
        var model;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!settings)
                        return [2 /*return*/];
                    model = __assign(__assign({}, settings), { emailDomain: domain });
                    return [4 /*yield*/, updateCompanySettings(model)];
                case 1:
                    _a.sent();
                    ToastrService.success(intl.formatMessage(messages.msgEmailDomainAddedVerificationPending, {
                        domain: domain.domain,
                    }));
                    return [2 /*return*/];
            }
        });
    }); }, [settings, updateCompanySettings, intl]);
    var handleEmailDomainVerifyResult = useCallback(function (result) { return __awaiter(void 0, void 0, void 0, function () {
        var model;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!settings || !result)
                        return [2 /*return*/];
                    model = __assign(__assign({}, settings), { emailUseCustomFrom: result.status === 1, emailDomain: result });
                    return [4 /*yield*/, updateCompanySettings(model)];
                case 1:
                    _a.sent();
                    return [2 /*return*/];
            }
        });
    }); }, [settings, updateCompanySettings]);
    var hideLedgerSetting = useMemo(function () { return getHideLedgerSetting(); }, []);
    useEffect(function () {
        var companyUpdatedHandler = function (company) {
            if (!settings || settings.companyId !== company.companyId)
                return;
            queryClient.invalidateQueries([SettingsQueryKey.CompanySettings, selectedCompanyId]);
        };
        SignalRService.subscribe('companyUpdated', companyUpdatedHandler);
        return function () { return SignalRService.unsubscribe('companyUpdated', companyUpdatedHandler); };
    }, [settings, selectedCompanyId, queryClient]);
    return (_jsxs("div", __assign({ id: "CompanyPage" }, { children: [_jsx(SettingsCompanySelector, { companies: companies || [], selectedCompanyId: selectedCompanyId, onCompanyChange: handleCompanyChange }), _jsx("div", __assign({ className: "panel" }, { children: _jsxs("div", __assign({ className: "panel-body" }, { children: [selectedCompany && _jsx(CompanyHeader, { company: selectedCompany }), _jsx("div", __assign({ className: "settings-page" }, { children: _jsxs(LoadingContainer, __assign({ isLoading: (canReadSettings && isLoading) ||
                                    (canReadSettings && isDocSettingsLoading) ||
                                    (canReadHrmSettings && isHrmLoading) }, { children: [_jsx(GeneralSection, { settings: settings, documentSettings: documentSettings, onFieldUpdate: handleFieldUpdate, onDocFieldUpdate: handleDocFieldUpdate, onDocSettingsUpdate: handleDocSettingsUpdate, onEmailDomainVerify: handleEmailDomainVerify, onEmailDomainDelete: handleEmailDomainDelete, onEmailDomainAdded: handleEmailDomainAdded, onEmailDomainVerifyResult: handleEmailDomainVerifyResult, selectedCompany: selectedCompany, connectWebUrl: connectWebUrl }), _jsx(BillingSetupSection, { settings: settings, documentSettings: documentSettings, onFieldUpdate: handleFieldUpdate, onDocFieldUpdate: handleDocFieldUpdate, onFieldUpdateWithConfirm: handleFieldUpdateWithConfirm, hideLedgerSetting: hideLedgerSetting, connectWebUrl: connectWebUrl }), _jsx(InvoicePdfSection, { settings: settings, onFieldUpdate: handleFieldUpdate, connectWebUrl: connectWebUrl }), _jsx(BillingEmailsSection, { settings: settings, templates: templates || [], onFieldUpdate: handleFieldUpdate, selectedCompany: selectedCompany }), _jsx(PaymentMonitoringSection, { settings: settings, templates: templates || [], onFieldUpdate: handleFieldUpdate, connectWebUrl: connectWebUrl }), _jsx(BudgetSettingsSection, { settings: settings, onFieldUpdate: handleFieldUpdate }), _jsx(TaskManagementSection, { settings: settings, resources: resources || [], onFieldUpdate: handleFieldUpdate }), _jsx(DocumentsSection, { settings: settings, documentSettings: documentSettings, onFieldUpdate: handleFieldUpdate, onDocFieldUpdate: handleDocFieldUpdate }), _jsx(ComplianceSection, { settings: settings, onComplianceUpdate: handleComplianceUpdate, onFieldUpdate: handleFieldUpdate }), _jsx(HrmSettingsSection, { hrmSettings: hrmSettings, onHrmFieldUpdate: handleHrmFieldUpdate, selectedCompanyId: selectedCompanyId })] })) }))] })) }))] })));
};
export default GeneralSettingsContainer;
